/**
 * Toolbar icons for the editor, hand-rolled as Lucide outlines to match the plugin-v2
 * icon set (packages/plugin-v2/app/components/svgs/icons.tsx) and the Make prototype.
 * Kept package-local so the editor stays self-contained — no CSS files, no dependency
 * on a consumer's icon library. Icons inherit color via `currentColor`.
 */
interface IIconProps {
	size?: number;
}

const baseProps = {
	fill: 'none',
	stroke: 'currentColor',
	strokeWidth: 2,
	strokeLinecap: 'round',
	strokeLinejoin: 'round',
	'aria-hidden': true,
} as const;

/** Lucide `bold`. */
export const BoldIcon = ({ size = 16 }: IIconProps) => (
	<svg width={size} height={size} viewBox='0 0 24 24' {...baseProps}>
		<path d='M6 12h9a4 4 0 0 1 0 8H7a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h6a4 4 0 0 1 0 8' />
	</svg>
);

/** Lucide `italic`. */
export const ItalicIcon = ({ size = 16 }: IIconProps) => (
	<svg width={size} height={size} viewBox='0 0 24 24' {...baseProps}>
		<line x1='19' x2='10' y1='4' y2='4' />
		<line x1='14' x2='5' y1='20' y2='20' />
		<line x1='15' x2='9' y1='4' y2='20' />
	</svg>
);

/** Lucide `underline`. */
export const UnderlineIcon = ({ size = 16 }: IIconProps) => (
	<svg width={size} height={size} viewBox='0 0 24 24' {...baseProps}>
		<path d='M6 4v6a6 6 0 0 0 12 0V4' />
		<line x1='4' x2='20' y1='20' y2='20' />
	</svg>
);

/** Lucide `list` — bullet list / the Lists dropdown trigger. */
export const ListIcon = ({ size = 16 }: IIconProps) => (
	<svg width={size} height={size} viewBox='0 0 24 24' {...baseProps}>
		<line x1='8' x2='21' y1='6' y2='6' />
		<line x1='8' x2='21' y1='12' y2='12' />
		<line x1='8' x2='21' y1='18' y2='18' />
		<line x1='3' x2='3.01' y1='6' y2='6' />
		<line x1='3' x2='3.01' y1='12' y2='12' />
		<line x1='3' x2='3.01' y1='18' y2='18' />
	</svg>
);

/** Lucide `list-ordered` — numbered list. */
export const ListOrderedIcon = ({ size = 16 }: IIconProps) => (
	<svg width={size} height={size} viewBox='0 0 24 24' {...baseProps}>
		<line x1='10' x2='21' y1='6' y2='6' />
		<line x1='10' x2='21' y1='12' y2='12' />
		<line x1='10' x2='21' y1='18' y2='18' />
		<path d='M4 6h1v4' />
		<path d='M4 10h2' />
		<path d='M6 18H4c0-1 2-2 2-3s-1-1.5-2-1' />
	</svg>
);

/** Lucide `braces` — merge field dropdown trigger. */
export const BracesIcon = ({ size = 16 }: IIconProps) => (
	<svg width={size} height={size} viewBox='0 0 24 24' {...baseProps}>
		<path d='M8 3H7a2 2 0 0 0-2 2v5a2 2 0 0 1-2 2 2 2 0 0 1 2 2v5c0 1.1.9 2 2 2h1' />
		<path d='M16 21h1a2 2 0 0 0 2-2v-5c0-1.1.9-2 2-2a2 2 0 0 1-2-2V5a2 2 0 0 0-2-2h-1' />
	</svg>
);

/** Lucide `chevron-down` — appended to dropdown triggers. */
export const ChevronDownIcon = ({ size = 14 }: IIconProps) => (
	<svg width={size} height={size} viewBox='0 0 24 24' {...baseProps}>
		<path d='m6 9 6 6 6-6' />
	</svg>
);

/** Lucide `link`. */
export const LinkIcon = ({ size = 16 }: IIconProps) => (
	<svg width={size} height={size} viewBox='0 0 24 24' {...baseProps}>
		<path d='M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71' />
		<path d='M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71' />
	</svg>
);
